/**
 * useUserData Hook
 *
 * Centralized hook for accessing and formatting user data consistently
 * across all components. Handles field mapping from backend to frontend.
 */

import { useAuth } from "../contexts/AuthContext";

export const useUserData = () => {
  const { user, isAuthenticated, isLoading, token } = useAuth();

  // Format user data with consistent field mapping
  const userData = user
    ? {
        // Display name - prefer full name over username
        displayName: user.full_name || user.username || null,

        // Raw fields for specific use cases
        username: user.username || null,
        fullName: user.full_name || null,
        email: user.email || null,
        userId: user.user_id || null,

        // Image URL - using backend field name
        avatarUrl: user.profile_image_url || null,

        // Helper flags
        hasAvatar: !!user.profile_image_url,
        hasFullName: !!user.full_name,

        // Avatar fallback character (first letter of username - consistently)
        avatarFallback: user.username?.charAt(0)?.toUpperCase() || null,
      }
    : null;

  return {
    // Formatted user data
    userData,

    // Auth state
    isAuthenticated,
    isLoading,
    hasToken: !!token,

    // Raw user object (for edge cases)
    rawUser: user,

    // Helper functions
    getHeaderProps: () => ({
      username: isAuthenticated ? userData?.displayName : null,
      user_image_url: isAuthenticated ? userData?.avatarUrl : null,
      avatarInitial: isAuthenticated ? userData?.avatarFallback : null,
    }),

    getProfileProps: () => ({
      username: userData?.username, // Raw username for profile page
      userImageUrl: userData?.avatarUrl,
      fullName: userData?.fullName,
      email: userData?.email,
      avatarInitial: userData?.avatarFallback,
    }),

    // Generic props for most components (username + userImageUrl pattern)
    getComponentProps: () => ({
      username: isAuthenticated ? userData?.displayName : null,
      userImageUrl: isAuthenticated ? userData?.avatarUrl : null,
      avatarInitial: isAuthenticated ? userData?.avatarFallback : null,
    }),

    // Generic props for Header-style components (username + user_image_url pattern)
    getHeaderStyleProps: () => ({
      username: isAuthenticated ? userData?.displayName : null,
      user_image_url: isAuthenticated ? userData?.avatarUrl : null,
      avatarInitial: isAuthenticated ? userData?.avatarFallback : null,
    }),
  };
};

export default useUserData;
